<template>
  <div class="space-y-4 pb-32">
    <!-- Breadcrumbs -->
    <div v-if="showChrome" class="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-1 text-sm">
      <span v-if="breadcrumbs.length === 0" class="text-gray-900 font-medium">All Entities</span>
      <button v-else @click="navigateToRoot" class="text-blue-600 hover:underline">All Entities</button>
      <span v-for="(crumb, i) in breadcrumbs" :key="crumb.id" class="flex items-center gap-1">
        <span class="text-gray-400">/</span>
        <button v-if="i < breadcrumbs.length - 1" @click="navigateTo(i)" class="text-blue-600 hover:underline">{{ crumb.name }}</button>
        <span v-else class="text-gray-900 font-medium">{{ crumb.name }}</span>
        <span class="text-xs text-gray-400">({{ crumb.type }})</span>
      </span>
      <div v-if="entityId && entityParents.length" class="relative ml-2">
        <button
          @click="showParentMenu = !showParentMenu"
          class="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
        >Parents...</button>
        <div v-if="showParentMenu" class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[240px]">
          <button
            v-for="parent in entityParents"
            :key="parent.id"
            @click="navigateToEntity(parent.id); showParentMenu = false"
            class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
          >
            <span class="text-gray-800">{{ parent.name }}</span>
            <span class="text-xs text-gray-400 ml-4">{{ parent.entity_type_name }}</span>
          </button>
        </div>
        <div v-if="showParentMenu" class="fixed inset-0 z-10" @click="showParentMenu = false"></div>
      </div>
      <router-link
        v-if="entityId"
        :to="{ name: 'Entity', params: { entity_id: entityId } }"
        class="ml-auto text-accent hover:underline whitespace-nowrap"
      >View Entity ↗</router-link>
    </div>

    <!-- Year + Contract filters -->
    <div v-if="showChrome && showYearFilter" class="flex items-center gap-3">
      <div class="inline-flex bg-white border border-gray-200 rounded-lg px-4 py-2 items-center gap-2 text-sm">
        <label class="font-medium text-gray-700">Year</label>
        <select v-model="year" class="input-default text-sm">
          <option v-for="yr in availableYears" :key="yr" :value="yr">{{ yr }}</option>
        </select>
      </div>
    </div>

    <!-- Config-driven sections with half/third-width support -->
    <template v-if="year && pageConfigLoaded">
    <template v-for="(row, rowIndex) in sectionRows" :key="rowIndex">
      <div :class="row.length === 3 ? 'grid grid-cols-3 gap-3 items-stretch' : row.length === 2 ? 'grid grid-cols-2 gap-4 items-stretch' : ''">
        <div v-for="section in row" :key="section.id" class="flex flex-col">
          <!-- Components that draw their own card render bare; components flagged
               selfChrome:false get the standard insight panel (border + titled
               header) applied here, so every section looks consistent without
               each widget re-implementing chrome. -->
          <component
            v-if="sectionProvidesChrome(section)"
            class="flex-1"
            :is="getComponent(section.component)"
            v-bind="{ ...pageContext, ...section.settings }"
            @drill-entity="drillIntoEntity"
            @select-patient="openPatientDrawer"
          />
          <div v-else class="flex-1 bg-white border border-gray-200 rounded-lg flex flex-col">
            <div class="px-4 py-3 bg-blue-50 border-b border-blue-100 rounded-t-lg">
              <h3 class="font-medium text-blue-900 text-sm">{{ sectionTitle(section) }}</h3>
            </div>
            <div class="flex-1 p-3">
              <component
                :is="getComponent(section.component)"
                v-bind="{ ...pageContext, ...section.settings }"
                @drill-entity="drillIntoEntity"
                @select-patient="openPatientDrawer"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    </template>

    <!-- Patient Drawer -->
    <div
      v-if="selectedPatientId"
      class="fixed inset-0 z-50 flex justify-end"
      @click.self="closePatientDrawer"
    >
      <div class="bg-black/25 absolute inset-0" @click="closePatientDrawer"></div>
      <div class="relative w-[720px] h-full bg-white shadow-xl overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <span class="font-medium text-sm text-gray-900">Patient Details</span>
          <button @click="closePatientDrawer" class="text-gray-400 hover:text-gray-600">&times;</button>
        </div>
        <Patient_Drawer
          :patient-identifier="selectedPatientId"
          :entity-id="entityId"
          :sidebar-mode="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getComponent, getComponentMeta } from '@/config/configurableComponentRegistry';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import ComposableAlertTable from '@/components/InsightsNew/ComposableAlertTable.vue';

const DEFAULT_METRIC_SECTIONS = [
  { id: 'trend', component: 'PerformanceTrend', order: 0, visible: true, width: 'half' },
  { id: 'cohort', component: 'PerformanceCohort', order: 1, visible: true, width: 'half', requires_entity: true, settings: { maxRows: 10 } },
  { id: 'alerts', component: 'ComposableAlertTable', order: 2, visible: true },
  { id: 'summary', component: 'PerformanceSummary', order: 3, visible: true },
  { id: 'insights', component: 'MetricInsights', order: 4, visible: true },
  { id: 'worklist', component: 'PatientWorklist', order: 5, visible: true },
];

const DEFAULT_DOMAIN_SECTIONS = [
  { id: 'trend', component: 'PerformanceTrend', order: 0, visible: true },
  { id: 'summary', component: 'PerformanceSummary', order: 1, visible: true },
  { id: 'blockers', component: 'PerformanceBlockers', order: 2, visible: true, requires_entity: true },
];

const DEFAULT_OVERALL_SECTIONS = [
  { id: 'summary', component: 'PerformanceSummary', order: 0, visible: true },
  { id: 'ai_insights', component: 'AIInsights', order: 1, visible: true, settings: {} },
];

export default {
  name: 'InsightsNew_MetricView',
  components: { Patient_Drawer, ComposableAlertTable },
  props: {
    pageType: { type: String, default: 'metric_page' },
    metricIds: { type: Array, default: () => [] },
    performanceDomainIds: { type: Array, default: () => [] },
    isDomain: { type: Boolean, default: false },
    entityIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      year: 2026,
      entityId: this.entityIds?.length ? this.entityIds[this.entityIds.length - 1] : null,
      breadcrumbs: [],
      datesByYear: {},
      selectedPatientId: null,
      entityParents: [],
      showParentMenu: false,
      sections: null,
      pageLayoutConfig: null,
      pageConfigLoaded: false,
    };
  },
  computed: {
    defaultSections() {
      if (this.pageType === 'overall_performance') return DEFAULT_OVERALL_SECTIONS;
      if (this.pageType === 'domain_page') return DEFAULT_DOMAIN_SECTIONS;
      return DEFAULT_METRIC_SECTIONS;
    },
    activeSections() {
      return this.sections || this.defaultSections;
    },
    showChrome() {
      return !this.pageLayoutConfig?.hide_chrome;
    },
    showYearFilter() {
      return this.pageLayoutConfig?.hide_year_filter !== true;
    },
    availableYears() {
      return Object.keys(this.datesByYear).map(Number).sort((a, b) => b - a);
    },
    contextId() {
      if (this.pageType === 'overall_performance') return null;
      if (this.pageType === 'domain_page' && this.performanceDomainIds.length) return this.performanceDomainIds[0];
      if (this.metricIds.length) return this.metricIds[0];
      return null;
    },
    pageContext() {
      return {
        metricId: this.metricIds.length ? this.metricIds[0] : null,
        metricIds: this.metricIds,
        performanceDomainId: this.performanceDomainIds.length ? this.performanceDomainIds[0] : null,
        performanceDomainIds: this.performanceDomainIds,
        isDomain: this.isDomain,
        year: this.year,
        entityId: this.entityId,
      };
    },
    visibleSections() {
      return this.activeSections
        .filter(s => s.visible !== false)
        .filter(s => !s.requires_entity || this.entityId)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    },
    sectionRows() {
      const rows = [];
      let currentPartialRow = [];
      let currentPartialWidth = null;
      for (const section of this.visibleSections) {
        if (section.width === 'half' || section.width === 'third') {
          const targetCount = section.width === 'half' ? 2 : 3;
          if (currentPartialWidth && currentPartialWidth !== section.width) {
            rows.push(currentPartialRow);
            currentPartialRow = [];
          }
          currentPartialWidth = section.width;
          currentPartialRow.push(section);
          if (currentPartialRow.length === targetCount) {
            rows.push(currentPartialRow);
            currentPartialRow = [];
            currentPartialWidth = null;
          }
        } else {
          if (currentPartialRow.length > 0) {
            rows.push(currentPartialRow);
            currentPartialRow = [];
            currentPartialWidth = null;
          }
          rows.push([section]);
        }
      }
      if (currentPartialRow.length > 0) rows.push(currentPartialRow);
      return rows;
    },
  },
  watch: {
    metricIds() {
      this.entityId = this.entityIds?.length ? this.entityIds[this.entityIds.length - 1] : null;
      this.breadcrumbs = [];
      this.selectedPatientId = null;
      this.fetchPageConfig();
    },
    performanceDomainIds() {
      this.entityId = this.entityIds?.length ? this.entityIds[this.entityIds.length - 1] : null;
      this.breadcrumbs = [];
      this.fetchPageConfig();
    },
    entityIds(ids) {
      this.entityId = ids?.length ? ids[ids.length - 1] : null;
      this.loadBreadcrumbs(ids);
      this.fetchEntityParents();
    },
  },
  methods: {
    getComponent,
    // A section's component draws its own card unless the registry flags
    // selfChrome:false (or the section overrides via settings.selfChrome).
    sectionProvidesChrome(section) {
      if (section.settings && section.settings.selfChrome != null) {
        return section.settings.selfChrome;
      }
      const meta = getComponentMeta(section.component);
      return !meta || meta.selfChrome !== false;
    },
    // Panel title when the layout supplies chrome: explicit section title wins,
    // else the registry displayName, else the raw component id.
    sectionTitle(section) {
      if (section.settings && section.settings.title) return section.settings.title;
      const meta = getComponentMeta(section.component);
      return (meta && meta.displayName) || section.component;
    },
    async fetchPageConfig() {
      if (!this.contextId && this.pageType !== 'overall_performance') {
        this.pageConfigLoaded = true;
        return;
      }
      this.pageConfigLoaded = false;
      try {
        const params = { page_type: this.pageType };
        if (this.contextId) params.context_id = this.contextId;
        const response = await axios.get('/api/page-configurations/resolve/', { params });
        this.pageLayoutConfig = response.data.layout_config || null;
        this.sections = this.pageLayoutConfig ? this.pageLayoutConfig.sections : null;
        if (this.pageLayoutConfig?.defaultYear) {
          this.year = this.pageLayoutConfig.defaultYear;
        }
      } catch {
        this.pageLayoutConfig = null;
        this.sections = null;
      } finally {
        this.pageConfigLoaded = true;
      }
    },
    async fetchObservationDates() {
      try {
        const response = await axios.post('api/impact_analytics/observation-dates/', {});
        this.datesByYear = response.data.dates_by_year;
      } catch (error) {
        console.error('Error fetching observation dates:', error);
      }
    },
    drillIntoEntity(row) {
      if (!row.entity_id) return;
      this.breadcrumbs.push({ id: row.entity_id, name: row.entity_name, type: row.entity_type });
      const entityIds = this.breadcrumbs.map(b => b.id);
      this.pushEntityRoute(entityIds);
    },
    navigateToRoot() {
      this.breadcrumbs = [];
      this.pushEntityRoute([]);
    },
    navigateTo(index) {
      this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
      const entityIds = this.breadcrumbs.map(b => b.id);
      this.pushEntityRoute(entityIds);
    },
    pushEntityRoute(entityIds) {
      this.entityId = entityIds.length ? entityIds[entityIds.length - 1] : null;
      const prefix = 'Insight';
      const metricId = this.metricIds.length === 1 ? this.metricIds[0] : null;
      const domainId = this.performanceDomainIds.length ? this.performanceDomainIds[0] : null;
      if (metricId && !domainId) {
        if (entityIds.length) {
          this.$router.push({ name: `${prefix} - Entity - Metric`, params: { metricId, entityIds } });
        } else {
          this.$router.push({ name: `${prefix} - Tenant - Metric`, params: { metricId } });
        }
      } else if (domainId) {
        if (entityIds.length) {
          this.$router.push({ name: `${prefix} - Entity - Domain`, params: { performanceDomainId: domainId, entityIds } });
        } else {
          this.$router.push({ name: `${prefix} - Tenant - Domain`, params: { performanceDomainId: domainId } });
        }
      }
    },
    async fetchEntityParents() {
      this.entityParents = [];
      if (!this.entityId) return;
      try {
        const response = await axios.get(`api/entities/${this.entityId}/parents/`);
        this.entityParents = response.data;
      } catch (error) {
        console.error('Error fetching entity parents:', error);
      }
    },
    navigateToEntity(entityId) {
      this.breadcrumbs = [];
      this.pushEntityRoute([entityId]);
    },
    async loadBreadcrumbs(entityIds) {
      if (!entityIds?.length) {
        this.breadcrumbs = [];
        return;
      }
      try {
        const responses = await Promise.all(
          entityIds.map(id => axios.get(`api/entities/${id}/`))
        );
        this.breadcrumbs = responses.map(r => ({
          id: r.data.id,
          name: r.data.name,
          type: r.data.entity_type_name,
        }));
      } catch (error) {
        console.error('Error loading breadcrumbs:', error);
        this.breadcrumbs = entityIds.map(id => ({ id, name: `Entity ${id}`, type: '' }));
      }
    },
    openPatientDrawer({ bene_mbi_id }) {
      this.selectedPatientId = bene_mbi_id;
    },
    closePatientDrawer() {
      this.selectedPatientId = null;
    },
  },
  mounted() {
    this.fetchObservationDates();
    this.fetchPageConfig();
    if (this.entityIds?.length) {
      this.loadBreadcrumbs(this.entityIds);
      this.fetchEntityParents();
    }
  },
};
</script>
